import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  Users, 
  CreditCard, 
  TrendingUp, 
  PieChart, 
  DollarSign, 
  BarChart3, 
  MapPin,
  Star,
  Target,
  Clock,
  Building
} from 'lucide-react';

const KPISlider = ({ dashboardData, loading }) => {
  const { t } = useTranslation('dashboard');

  // formatCurrency function
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Helper function for colored percentage display
  const formatPercentage = (value, context = '') => {
    const color = value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-600';
    const sign = value > 0 ? '+' : '';
    const contextText = context ? ` (${context})` : '';
    return `<span class="${color} font-medium">${sign}${value}%</span>${contextText}`;
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  if (!dashboardData) {
    return <div>Loading KPI data...</div>;
  }

  // Calculated values
  const yearlyRent = dashboardData.totalRent * 12;
  const avgRentPerUnit = dashboardData.units > 0 ? dashboardData.totalRent / dashboardData.units : 0;
  
  // Correct €/m² calculation using real data
  const avgRentPerSqm = dashboardData.totalSqm > 0 ? dashboardData.totalRent / dashboardData.totalSqm : 0;
  
  // New KPI calculations using real data
  const topResidentialTenant = dashboardData.topResidentialTenant;
  const topCommercialTenant = dashboardData.topCommercialTenant;
  const topAreaTenant = dashboardData.topAreaTenant;
  
  const avgRentalDurationResidential = 4.2; // Average years for residential
  const avgRentalDurationCommercial = 6.8; // Average years for commercial

  // All KPI data with metallic Analytics-style backgrounds - COMPLETELY UNCHANGED
  const allKPIs = [
    // Set 1: Core Metrics
    {
      title: "Eigentümer & Objekte",
      value: loading ? "..." : dashboardData.properties,
      subtitle: "Immobilien",
      details: [
        `${dashboardData.units} Einheiten`,
        `${dashboardData.owners} Eigentümer`
      ],
      icon: Home,
      bgColor: "bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200 dark:from-slate-800 dark:via-blue-900/20 dark:to-slate-700",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-blue-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Mieter & Flächen",
      value: loading ? "..." : dashboardData.tenants,
      subtitle: "Mieter",
      details: [
        `${dashboardData.totalSqm?.toLocaleString('de-DE') || 0} m² gesamt`,
        `${Math.round(dashboardData.occupancyRate)}% Auslastung`
      ],
      icon: Users,
      bgColor: "bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 dark:from-emerald-900/20 dark:via-green-900/20 dark:to-emerald-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-green-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Mieteinnahmen",
      value: loading ? "..." : formatCurrency(dashboardData.totalRent),
      subtitle: "Monatlich",
      details: [
        `${formatCurrency(yearlyRent)} jährlich`,
        `${formatCurrency(avgRentPerUnit)}/Einheit`
      ],
      icon: CreditCard,
      bgColor: "bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-100 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-indigo-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-indigo-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "ROI Performance",
      value: loading ? "..." : `${dashboardData.roi?.toFixed(1) || 0}%`,
      subtitle: "Portfolio-ROI",
      details: [
        dashboardData.roi > 15 ? "Überdurchschnittlich" : "Solide",
        `Gewerbe: ${dashboardData.roiCommercial?.toFixed(1) || 0}%`,
        `Wohnen: ${dashboardData.roiResidential?.toFixed(1) || 0}%`
      ],
      icon: TrendingUp,
      bgColor: "bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 dark:from-purple-900/20 dark:via-violet-900/20 dark:to-purple-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-purple-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Cashflow",
      value: loading ? "..." : formatCurrency(dashboardData.totalRent * 0.75),
      subtitle: "Monatlich",
      details: [
        `${formatCurrency(dashboardData.totalRent * 0.75 * 12)} jährlich`,
        "75% der Mieteinnahmen"
      ],
      icon: DollarSign,
      bgColor: "bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-green-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-green-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Wartungskosten",
      value: loading ? "..." : formatCurrency(dashboardData.totalRent * 0.051),
      subtitle: "Monatlich",
      details: [
        "5.1% der Mieteinnahmen",
        `${formatCurrency(dashboardData.totalRent * 0.051 / (dashboardData.units || 1))}/Einheit`
      ],
      icon: Building,
      bgColor: "bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 dark:from-orange-900/20 dark:via-amber-900/20 dark:to-orange-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-orange-500/20",
      shadowColor: "shadow-lg"
    },

    // Set 3: Portfolio Analysis - COMPLETELY UNCHANGED
    {
      title: "Objektverteilung",
      value: loading ? "..." : dashboardData.properties,
      subtitle: "Immobilien",
      details: [
        `${dashboardData.units} Einheiten gesamt`,
        `Ø ${Math.round((dashboardData.units || 0) / (dashboardData.properties || 1))} Einheiten/Objekt`,
        `${dashboardData.owners} Eigentümer`
      ],
      icon: PieChart,
      bgColor: "bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 dark:from-blue-900/20 dark:via-cyan-900/20 dark:to-blue-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-blue-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Verwaltete Kautionen",
            value: loading ? "..." : formatCurrency(dashboardData.totalDeposits || 0),
      subtitle: "Gesamt",
      details: [
                `${dashboardData.tenants || 0} Kautionen`,
        `Ø ${formatCurrency((dashboardData.totalDeposits || 0) / (dashboardData.tenants || 1))}/Kaution`,
        "Sicher verwahrt"
      ],
      icon: Target,
      bgColor: "bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-100 dark:from-teal-900/20 dark:via-cyan-900/20 dark:to-teal-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-teal-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Durchschnittsmiete",
      value: loading ? "..." : `${avgRentPerSqm.toFixed(2)} €/m²`,
      subtitle: "Pro Quadratmeter",
      details: [
        `${formatCurrency(avgRentPerUnit)} pro Einheit`,
        `${dashboardData.totalSqm?.toLocaleString('de-DE') || 0} m² gesamt`
      ],
      icon: BarChart3,
      bgColor: "bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 dark:from-amber-900/20 dark:via-yellow-900/20 dark:to-amber-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-yellow-500/20",
      shadowColor: "shadow-lg"
    },

    // Set 4: Top Performers
    {
      title: "Top Wohnmieter",
      value: loading ? "..." : (topResidentialTenant ? `${(topResidentialTenant.cold_rent / topResidentialTenant.unit_size).toFixed(2)} €/m²` : "N/A"),
      subtitle: "Höchste Miete/m²",
      details: [
        topResidentialTenant ? `${formatCurrency(topResidentialTenant.cold_rent)} Miete` : "Keine Daten",
        topResidentialTenant ? `${topResidentialTenant.unit_size} m²` : ""
      ],
      icon: Star,
      bgColor: "bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-indigo-900/20 dark:via-blue-900/20 dark:to-indigo-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-indigo-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Top Gewerbemieter",
      value: loading ? "..." : (topCommercialTenant ? `${(topCommercialTenant.cold_rent / topCommercialTenant.unit_size).toFixed(2)} €/m²` : "N/A"),
      subtitle: "Höchste Miete/m²",
      details: [
        topCommercialTenant ? `${formatCurrency(topCommercialTenant.cold_rent)} Miete` : "Keine Daten",
        topCommercialTenant ? `${topCommercialTenant.unit_size} m²` : ""
      ],
      icon: Building,
      bgColor: "bg-gradient-to-br from-orange-50 via-red-50 to-orange-100 dark:from-orange-900/20 dark:via-red-900/20 dark:to-orange-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-orange-500/20",
      shadowColor: "shadow-lg"
    },
    {
      title: "Größte Einheit",
      value: loading ? "..." : (topAreaTenant ? `${topAreaTenant.unit_size} m²` : "N/A"),
      subtitle: "Nach Fläche",
      details: [
        topAreaTenant ? `${formatCurrency(topAreaTenant.cold_rent)} Miete` : "Keine Daten",
        topAreaTenant ? `${(topAreaTenant.cold_rent / topAreaTenant.unit_size).toFixed(2)} €/m²` : ""
      ],
      icon: MapPin,
      bgColor: "bg-gradient-to-br from-lime-50 via-green-50 to-lime-100 dark:from-lime-900/20 dark:via-green-900/20 dark:to-lime-800/30",
      textColor: "text-gray-900 dark:text-white",
      iconBg: "bg-lime-500/20",
      shadowColor: "shadow-lg"
    }
  ];

  const filteredKPIs = allKPIs.filter(kpi => {
    if (loading) return true;
    switch (kpi.title) {
      case "ROI Performance":
        return dashboardData.roi && dashboardData.roi > 0;
      case "Verwaltete Kautionen":
        return dashboardData.totalDeposits && dashboardData.totalDeposits > 0;
      default:
        return true;
    }
  });

  const itemsPerPage = 3;
  const totalPages = Math.ceil(filteredKPIs.length / itemsPerPage);

  // Navigation functions
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalPages) % totalPages);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Auto-advance every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full">
      {/* Navigation Arrows - COMPLETELY UNCHANGED */}
      <button
        onClick={prevSlide}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-all duration-200"
        aria-label="Previous KPIs"
      >
        <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white dark:hover:bg-gray-700 transition-all duration-200"
        aria-label="Next KPIs"
      >
        <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
      </button>

      {/* KPI Cards Container - COMPLETELY UNCHANGED */}
      <div 
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {Array.from({ length: totalPages }, (_, pageIndex) => (
            <div key={pageIndex} className="w-full flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-12">
                {filteredKPIs
                  .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                  .map((kpi, index) => {
                    const IconComponent = kpi.icon;
                    
                    return (
                      <div
                        key={index}
                        className={`${kpi.bgColor} ${kpi.shadowColor} rounded-xl p-6 border border-white/20 dark:border-gray-700/30 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-xl`}
                      >
                        {/* FIXED: Icon positioned absolutely, title centered across full width */}
                        <div className="relative mb-4">
                          {/* Icon positioned absolutely in top-left */}
                          <div className={`absolute top-0 left-0 ${kpi.iconBg} p-2 rounded-lg`}>
                            <IconComponent className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                          </div>
                          
                          {/* Title centered across full card width */}
                          <div className="text-center">
                            <h3 className={`text-sm font-medium ${kpi.textColor} opacity-80`}>
                              {kpi.title}
                            </h3>
                          </div>
                        </div>
                        
                        {/* Center: Hauptzahl größer und zentriert - COMPLETELY UNCHANGED */}
                        <div className="text-center space-y-2 mb-4">
                          <div className={`text-2xl font-bold ${kpi.textColor}`}>
                            {kpi.value}
                          </div>
                          <p className={`text-sm ${kpi.textColor} opacity-70`}>
                            {kpi.subtitle}
                          </p>
                        </div>

                        {/* FIXED: Bottom details - ONLY CHANGE: proper left/right distribution */}
                        <div className="grid grid-cols-2 gap-2 h-10">
                          <div className="space-y-1">
                            <p className={`text-xs ${kpi.textColor} opacity-60 leading-tight`}>
                              {kpi.details[0] || ''}
                            </p>
                            {kpi.details.length > 2 && (
                              <p className={`text-xs ${kpi.textColor} opacity-60 leading-tight`}>
                                {kpi.details[2] || ''}
                              </p>
                            )}
                          </div>
                          <div className="text-right space-y-1">
                            <p className={`text-xs ${kpi.textColor} opacity-60 leading-tight`}>
                              {kpi.details[1] || ''}
                            </p>
                            {kpi.details.length > 3 && (
                              <p className={`text-xs ${kpi.textColor} opacity-60 leading-tight`}>
                                {kpi.details[3] || ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Dots - COMPLETELY UNCHANGED */}
      <div className="flex justify-center mt-6 space-x-2">
        {Array.from({ length: totalPages }, (_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentIndex 
                ? 'bg-blue-500' 
                : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default KPISlider;
